import _ from 'lodash';
import moment from 'moment-timezone';

import { RPM_PATIENT_STATUS } from "../constants";
import { ACTIVATION_METHODS, CTAM, flatProgramsSubprogramsList, getDefaultActivationMethod, getOrderedActivationMethods, getProgramForApi, getSubprogramForApi, RPM, RTMBehavioralHealth, RTMMusculoskeletal, RTMRespiratory } from "./cmsPrograms";
import Strings from '../Strings';
import { ORG_FEATURES, orgFeatureEnabled } from "./userPermissions";
import { VITALS } from "./readings";
import { consentDraftToRecord, getConsentTemplate, hasRecordedConsent } from "./consentTemplates";

// Re-export ACTIVATION_METHODS for backwards compatibility
export { ACTIVATION_METHODS };
export { getDefaultActivationMethod, getOrderedActivationMethods };

const magicDateToClearEndDate = '2300-01-01T00:00:00Z';
export const HEART_RATE_VALUE = 'HeartRate';
const VITALS_DECODER_VERSION = 2;

// The subprogram can be preselected while the program is still empty.
export const getEnrollmentConsentTemplate = enrollment => getConsentTemplate(
  getProgramForApi(flatProgramsSubprogramsList.find(l => l.subprogram === enrollment?.subprogram)?.name) || enrollment?.program,
);
 
export const enrollmentFieldsEnum = {
  registrationType: {
    fieldId: 'registrationType',
    required: (...args) => !!args[2]?.registrationTypeSelectionRequired,
  },
  accessTrack: {
    fieldId: 'accessTrack',
    required: (...args) => !!args[2]?.accessRegistration,
  },
  // ACCESS consents live on the enrollment (a patient may agree for one enrollment and not
  // another). The care-update consent gates Care Update transmission (AT-07).
  careUpdateConsent: {
    fieldId: 'careUpdateConsent',
    includeFalsy: true,
    required: (...args) => !!args[2]?.accessRegistration,
  },
  treatmentPlan: {
    fieldId: 'treatmentPlan',
    required: () => false,
  },
  enrollmentId: {
    fieldId: 'enrollmentId',
    required: () => { return false },
    getPropertyForDTO: () => 'id',
  },
  subprogram: {
    fieldId: 'subprogram',
    required: () => { return true },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
    getPropertyForDTO: () => 'subprogram',
    toDTO: val => getSubprogramForApi(val.subprogram),
  },
  conditionId: {
    fieldId: 'conditionId',
    getPropertyForDTO: () => 'condition_id',
    required: () => { return true },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  enrollmentStart: {
    fieldId: 'enrollmentStart',
    required: (_, stage) => { return stage && (stage === RPM_PATIENT_STATUS.activated) },
    getPropertyForDTO: () => 'start_date',
    toDTO: (val, orgTimezone) => val.activation_method !== ACTIVATION_METHODS.Manual
    ? undefined
    : (val.enrollmentStart
     ? moment.tz(val.enrollmentStart, orgTimezone || moment.tz.guess()).startOf('day').format()
      : undefined),
  },
  enrollmentEnd: {
    fieldId: 'enrollmentEnd',
    required: () => { return false },
    getPropertyForDTO: () => 'end_date',
    toDTO: (val, orgTimezone) => val.enrollmentEnd
      ? moment.tz(val.enrollmentEnd, orgTimezone || moment.tz.guess()).startOf('day').format()
      : magicDateToClearEndDate,
  },
  kit_id: {
    fieldId: 'kit_id',
    required: (_, stage) => { return stage && (stage === RPM_PATIENT_STATUS.activated) },
  },
  enrollmentConsents: {
    fieldId: 'enrollmentConsents',
    getPropertyForDTO: e => e.verbalConsent ? 'verbal_consent' : e.writtenConsent ? 'written_consent' : 'consent_record',
    toDTO: e => hasRecordedConsent(e) ? true : consentDraftToRecord(e.consentDraft, getEnrollmentConsentTemplate(e)),
    required: () => { return true },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  default_program: {
    fieldId: 'default_program',
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  care_provider_org_uuid: {
    fieldId: 'care_provider_org_uuid',
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  care_provider: {
    fieldId: 'care_provider',
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  medication: {
    fieldId: 'medication',
    getPropertyForDTO: () => 'medications',
    toDTO: val => val.medication ? [val.medication] : [],
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  vitals: {
    fieldId: 'vitals',
    required: (val, stage) => {
      const selectVitalsDuringEnrollment = orgFeatureEnabled(ORG_FEATURES.select_vitals_during_enrollment);
      const selectVitalsDuringActivation = orgFeatureEnabled(ORG_FEATURES.select_vitals_during_activation);
      if ((stage === RPM_PATIENT_STATUS.enrolled && selectVitalsDuringEnrollment)
        || (stage === RPM_PATIENT_STATUS.activated && selectVitalsDuringActivation)) {
        if (val.subprogram === RPM) {
          return true;
        }
        if ([RTMMusculoskeletal, RTMBehavioralHealth, RTMRespiratory, CTAM].includes(val.subprogram)) {
          return false;
        }
      }
      return false;
    },
    getPropertyForDTO: () => 'allowed_vitals',
    toDTO: val => {
      if (val.subprogram === RPM) {
        return val.vitals?.length > 0
          ? _.uniq(val.vitals.flatMap(v => vitalModuleByValue(v)?.vitals || []))
          : []
      }
      if ([RTMMusculoskeletal, RTMBehavioralHealth, RTMRespiratory, CTAM].includes(val.subprogram)) {
        return vitalsForRTM[0].vitals;
      }
      return undefined;
    },
    readOnly: (_, stage) => {
      if (stage === RPM_PATIENT_STATUS.enrolled  || stage === RPM_PATIENT_STATUS.activated) {
        return false;
      }
      return true;
    },
  },
  activation_method: {
    fieldId: 'activation_method',
    required: (_, stage) => { return stage === RPM_PATIENT_STATUS.activated || stage === RPM_PATIENT_STATUS.enrolled },
  },
  shipment_method: {
    fieldId: 'shipment_method',
    required: (_, stage) => {
      const shippingEnabled = orgFeatureEnabled(ORG_FEATURES.shipper_enabled);
      return stage === RPM_PATIENT_STATUS.enrolled && shippingEnabled;
    },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  completion_sms_enabled: {
    fieldId: 'completion_sms_enabled',
    required: () => { return false },
  },
  terminated_reason: {
    fieldId: 'terminated_reason',
    required: () => { return false },
  },
  provider_order_no: {
    fieldId: 'provider_order_no',
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
  },
  separate_hr_device: {
    fieldId: 'separate_hr_device',
    required: () => { return false },
    readOnly: (_, stage) => {
      if (stage === RPM_PATIENT_STATUS.enrolled  || stage === RPM_PATIENT_STATUS.activated) {
        return false;
      }
      return true;
    },
    toDTO: val => val.subprogram === RPM
      ? !!val.separate_hr_device && getIncludedVitalsValues(val.vitals || []).includes(HEART_RATE_VALUE)
      : undefined,
  },
  rtm_device_model: {
    fieldId: 'rtm_device_model',
    required: () => { return false },
    readOnly: (_, stage) => { return stage !== RPM_PATIENT_STATUS.enrolled },
    toDTO: val => [RTMMusculoskeletal, RTMBehavioralHealth, RTMRespiratory, CTAM].includes(val.subprogram) ? val.rtm_device_model : undefined,
  },
  // Tells the backend this client decodes stored heart rate correctly, so its edits can't corrupt vitals.
  vitals_decoder: {
    fieldId: 'vitals_decoder',
    required: () => { return false },
    toDTO: () => VITALS_DECODER_VERSION,
  },
  clear_terminated_reason: {
    fieldId: 'clear_terminated_reason',
    required: () => { return false },
    toDTO: val => val.clear_terminated_reason ? true : undefined,
  },
};

const vitalsForRTM = [
  { label: Strings.takes, vitals: [VITALS.PILLS_TAKEN], group: 'pills_taken', value: 'PillsTaken'},
  { label: Strings.inhalationTaken, vitals: [VITALS.INHALATION_TAKEN], group: 'inhalations', value: 'InhalationTaken' },
];

export const vitalsForRPM = [
  { label: Strings.temperature, vitals: [VITALS.TEMPERATURE], group: 'temperature', value: 'Temperature' },
  { label: Strings.weight, vitals: [VITALS.WEIGHT], group: 'weight', value: 'Weight' },
  { label: Strings.bloodPressure, vitals: [VITALS.PRESSURE_SYSTOLIC, VITALS.PRESSURE_DIASTOLIC, VITALS.HEART_RATE], includes: [HEART_RATE_VALUE], group: 'blood_pressure', value: 'BloodPressure' },
  { label: Strings.heartRate, vitals: [VITALS.HEART_RATE], group: 'heart_rate', value: HEART_RATE_VALUE },
  { label: Strings.sleepDuration, vitals: [VITALS.SLEEP], group: 'sleep', value: 'Sleep' },
  { label: Strings.steps, vitals: [VITALS.STEPS], group: 'steps', value: 'Steps' },
  { label: Strings.glucoseConcentration, vitals: [VITALS.SUGAR_LEVEL], group: 'sugar_level', value: 'SugarLevel' },
  { label: Strings.oxygenSaturation, vitals: [VITALS.OXYGEN_SATURATION, VITALS.HEART_RATE], includes: [HEART_RATE_VALUE], group: 'oxygen_saturation', value: 'OxygenSaturation' },
  { label: Strings.spirometry, vitals: [VITALS.PEAK_EXPIRATORY_FLOW, VITALS.FORCED_EXPIRATORY_VOLUME_1S, VITALS.FORCED_VITAL_CAPACITY], group: 'spirometry', value: 'Spirometry' },
  { label: Strings.exerciseDetails, vitals: [VITALS.HEART_RATE, VITALS.STEPS, VITALS.EXERCISE_AMOUNT, VITALS.EXERCISE_DURATION, VITALS.BURNED_CALORIES, VITALS.DISTANCE], group: 'exercise_details', value: 'ExerciseDetails' },
  { label: Strings.airQuality, vitals: [VITALS.PM25], group: 'air_quality', value: 'AirQuality' },
  { label: Strings.coagulation, vitals: [VITALS.PROTHROMBIN_TIME, VITALS.INTERNATIONAL_NORMALIZED_RATIO], group: 'coagulation', value: 'Coagulation' },
  // { label: Strings.takes, vitals: [VITALS.PILLS_TAKEN], value: 'PillsTaken'},
];

export const allVitals = vitalsForRPM.concat(vitalsForRTM);

// The modules the RPM vitals dropdown offers. offered holds the device-mapping
// group keys the patient's organization can get a device for, or null when the
// lookup failed (Vitals is required, so every module is offered then). kept
// lists modules that stay whatever the catalog says: saved on the enrollment,
// or brought along by a picked module.
export const selectableVitalModules = (offered, kept) => (offered == null
  ? allVitals
  : allVitals.filter(m => offered.includes(m.group) || kept.includes(m.value)));

// The group keys in an offered-vitals response, or null when it isn't
// { groups: [{ key }] }; the form treats that as a failed lookup.
export const offeredVitalGroupKeys = response => (
  Array.isArray(response?.groups) && response.groups.every(g => typeof g?.key === 'string')
    ? response.groups.map(g => g.key)
    : null
);

export const getVitalsFromValues = vitals => {
  const ret = vitals?.map(vital => 
    allVitals.find(vi => vi.vitals.includes(vital)));
  return _.uniq(ret);
}

const vitalModuleByValue = value => allVitals.find(m => m.value === value);

// Modules another selected module already brings along (heart rate comes with a BP cuff or an oximeter).
export const getIncludedVitalsValues = values => _.uniq(values.flatMap(v => vitalModuleByValue(v)?.includes || []));

export const getVitalsValues = vitals => {
  const stored = vitals || [];
  const remaining = new Set(stored);
  const picked = [];
  // Whole modules first, biggest first: Exercise details claims heart_rate and steps, BP claims its own heart_rate.
  _.sortBy(allVitals, m => -m.vitals.length).forEach(m => {
    if (m.vitals.every(v => remaining.has(v))) {
      picked.push(m);
      m.vitals.forEach(v => remaining.delete(v));
    }
  });
  // Partial records (e.g. BP saved without heart_rate) fall back to the first module listing the vital.
  remaining.forEach(v => {
    const m = allVitals.find(o => o.vitals.includes(v));
    if (m && !picked.includes(m)) {
      picked.push(m);
    }
  });
  const included = getIncludedVitalsValues(picked.map(m => m.value));
  // Stored order is kept: the backend awaiting-kit list sorts by these labels in that order.
  const firstPosition = m => Math.min(...m.vitals.map(v => stored.indexOf(v)).filter(i => i >= 0));
  return picked
    .filter(m => !included.includes(m.value))
    .sort((a, b) => firstPosition(a) - firstPosition(b))
    .map(m => m.value);
};

// Explicit picks only; modules included by another pick are derived, never stored as a choice.
export const normalizeVitalsSelection = values => {
  const included = getIncludedVitalsValues(values);
  return values.filter(v => !included.includes(v));
};

// The vitals dropdown hands back everything it shows as checked, derived values included.
export const nextVitalsSelection = (explicit, visible) => {
  const derived = getIncludedVitalsValues(explicit).filter(v => !explicit.includes(v));
  return normalizeVitalsSelection(visible.filter(v => !derived.includes(v)));
};

export const getVitalsValuesFromEnrollment = (enrollment) => {
  if (enrollment.program === 'RPM') {
    return getVitalsValues(enrollment.allowedVitals);
  }
  return enrollment.allowedVitals;
};

export const getVitalsLabelsFromEnrollment = (enrollment, { withIncluded = false } = {}) => {
  if (!enrollment?.allowedVitals) {
    return [];
  }
  const values = getVitalsValues(enrollment.allowedVitals);
  const separateHeartRate = !!enrollment.separateHrDevice && getIncludedVitalsValues(values).includes(HEART_RATE_VALUE);
  if (!withIncluded) {
    // Module labels, one per device the shipper sends.
    const labels = values.map(v => vitalModuleByValue(v)?.label);
    return separateHeartRate ? [...labels, Strings.separateHeartRateDevice] : labels;
  }
  return _.uniq(values.flatMap(v => [v, ...(vitalModuleByValue(v)?.includes || [])]))
    .map(v => (v === HEART_RATE_VALUE && separateHeartRate
      ? `${vitalModuleByValue(v).label} (${Strings.separateDevice})`
      : vitalModuleByValue(v)?.label));
};

export const capTypesForRTM = [
  { label: 'Smart Cap (20-dram)', value: 'lsc' },
  { label: 'Large Smart Cap (30, 45, 60-dram)', value: 'lxl' },
  { label: 'Medication Organizer', value: 'lmo' },
];
export const defaultCapTypeForRTM = 'lsc';

export const enrollmentToDTO = (enrollment, orgTimezone) => {
  return Object.values(enrollmentFieldsEnum).reduce((acc, field) => {
    const value = enrollment[field.fieldId];
      acc[field.getPropertyForDTO ? field.getPropertyForDTO(enrollment) : field.fieldId] = field.toDTO ? field.toDTO(enrollment, orgTimezone) : value;
    return acc;
  }, {});
};

export const fixNewEnrollmentCondition = (enrollment, newConditions) => {
  if (!enrollment?.conditionObject || newConditions.length === 0) {
    return enrollment;
  }
  const conditionId = newConditions.find(nc => nc.code === enrollment.conditionObject.code)?.id;
  
  return {
    ...enrollment,
    conditionId: conditionId,
  }
};
